import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { useIsBrandAdmin } from '../lib/brandAdmin'
import { searchFilter } from '../lib/search'
import { loadOrderStates, poBaseStatus, SHIPPED_STATUSES } from '../lib/orderStatus'
import { supabase } from '../lib/supabase'
import { AlertTriangle, CheckCircle2, Download, RefreshCw, Search, X } from 'lucide-react'
import { format, parseISO } from 'date-fns'
import { runPlan, planWithOrderCycles, applyLotSplits, cashSchedule, addDays, TRANSFER_DAYS, FBA_MAX_WEEKS, isSplitProduct } from '../lib/planningEngine'
import HelpLink from '../components/HelpLink'

// Planner — Bloomi weeks-of-supply replenishment plan.
// Inputs: active forecast + latest inventory uploads (Planning Inputs page) + firm POs/shipments/transfers in the app.
// Outputs: WOS heatmap, reorder list, transfer plan, cash-out schedule. Selected recommendations become draft POs /
// planned transfers, which then count as firm supply on the next run.

const TABS = [
  { key: 'heatmap', label: 'WOS Heatmap' },
  { key: 'orders', label: 'Reorder List' },
  { key: 'transfers', label: 'Transfer Plan' },
  { key: 'cash', label: 'Cash Out' },
  { key: 'forecast', label: 'Forecast' },
]
const LOW_WOS = 8 // amber below this many weeks
// Warehouse target = 12 weeks of the warehouse's own Direct/Other sales. FBA transfer needs still drive supplier
// orders (timed to land before each transfer), but no extra FBA buffer is held at the warehouse. Decided Oct 2026.
const WAREHOUSE_BASIS = 'own'
// When warehouse stock can't cover everything in a month, who is served first. Per brand slug; default 'direct'.
// Bloomi: Amazon first (Oct 2026) — the team can always choose to ship less to FBA.
const SHORTAGE_PRIORITY = { bloomi: 'fba' }
// POs are Draft → Confirmed (or Cancelled); nothing is received in the app. Older stored values (sent,
// partially_received, received) read as Confirmed. Units dated before the inventory upload are assumed in the counts.
const OPEN_PO_STATUSES = ['draft', 'sent', 'confirmed', 'partially_received', 'received']
const CASH_PO_STATUSES = ['sent', 'confirmed', 'partially_received', 'received'] // Confirmed (incl. completed) POs count toward Cash Out
// Transfers are Planned → Shipped (or Cancelled). No receiving step: stock shows up in the uploads.
//   Planned  — out of the warehouse on its ship date, into FBA on its required-by date.
//   Shipped  — out of the warehouse only if it shipped after the warehouse upload (otherwise Shopify already
//              excludes it). Into FBA only if it shipped after the FBA upload; otherwise it is already inside
//              Amazon's own inbound number, which the plan counts as landing AMAZON_CHECKIN_DAYS after the upload.
const OPEN_TRANSFER_STATUSES = ['planned', 'shipped']
const AMAZON_CHECKIN_DAYS = 14

const fmtN = n => (n === null || n === undefined || Number.isNaN(n) ? '—' : Math.round(n).toLocaleString())
const fmt$ = n => (n === null || n === undefined ? '—' : n.toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }))
const fmtD = d => (d ? format(parseISO(d), 'MMM d, yyyy') : '—')
const fmtM = d => format(parseISO(d), "MMM ''yy") // "Jan '27" — "Jan 27" read like a date
// month range: "Jan–Mar '27", or "Nov '26–Jan '27" across years
const fmtMRange = (a, b) => (!b || a === b ? fmtM(a) : a.slice(0, 4) === b.slice(0, 4) ? `${format(parseISO(a), 'MMM')}–${fmtM(b)}` : `${fmtM(a)}–${fmtM(b)}`)
const today = () => format(new Date(), 'yyyy-MM-dd')

async function fetchAll(query, pageSize = 1000) {
  let from = 0, out = []
  for (;;) {
    const { data, error } = await query().range(from, from + pageSize - 1)
    if (error) throw error
    out = out.concat(data || [])
    if (!data || data.length < pageSize) return out
    from += pageSize
  }
}

function downloadCSV(name, header, rows) {
  const esc = v => { const s = v === null || v === undefined ? '' : String(v); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s }
  const csv = [header, ...rows].map(r => r.map(esc).join(',')).join('\n')
  const a = document.createElement('a')
  a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }))
  a.download = name; a.click(); URL.revokeObjectURL(a.href)
}

// ── load everything the engine needs ──────────────────────────────────────────
async function loadInputs(brandId) {
  const [prod, sup, bom, locs, oh, fi, snaps, pos, ships, tos] = await Promise.all([
    supabase.from('products').select('id, sku, name, asin, product_type, supplier_id, default_destination, target_wos_fba, target_wos_warehouse, cost_price, retail_price, is_active, shopify_enabled, shopify_replen_status, amazon_enabled, amazon_replen_status').eq('brand_id', brandId).is('deleted_at', null),
    supabase.from('suppliers').select('*').eq('brand_id', brandId),
    supabase.from('bill_of_materials').select('parent_product_id, component_product_id, quantity').eq('brand_id', brandId),
    supabase.from('locations').select('id, name, type, is_active').eq('brand_id', brandId).eq('is_active', true),
    supabase.from('inventory_on_hand').select('product_id, location_id, on_hand').eq('brand_id', brandId),
    supabase.from('forecast_imports').select('*').eq('brand_id', brandId).eq('is_active', true).maybeSingle(),
    supabase.from('inventory_snapshots').select('location_id, as_of_date, created_at, inbound').eq('brand_id', brandId).order('created_at', { ascending: false }).limit(50),
    supabase.from('purchase_orders').select('id, po_number, status, supplier_id, order_date, expected_date, po_line_items(id, product_id, sku, description, quantity_ordered, quantity_received, unit_cost)').eq('brand_id', brandId).in('status', OPEN_PO_STATUSES).is('deleted_at', null),
    supabase.from('shipments').select('id, po_id, status, ship_to_location_id, is_direct_ship, expected_arrival, expected_ship_date, actual_ship_date, shipment_line_items(po_line_item_id, product_id, quantity_shipped, total_units, quantity_ordered, quantity_received)').eq('brand_id', brandId).is('deleted_at', null).not('status', 'in', '(received,cancelled)'),
    supabase.from('transfer_orders').select('id, transfer_number, status, from_location_id, to_location_id, expected_ship_date, ship_date, expected_arrival, transfer_order_lines(product_id, quantity)').eq('brand_id', brandId).in('status', OPEN_TRANSFER_STATUSES),
  ])
  const firstError = [prod, sup, bom, locs, oh, fi, snaps, pos, ships, tos].find(r => r.error)
  if (firstError) throw firstError.error

  const products = prod.data || []
  const productById = Object.fromEntries(products.map(p => [p.id, p]))
  const fbaLoc = (locs.data || []).find(l => l.type === 'amazon_fba')
  const whLoc = (locs.data || []).find(l => l.type !== 'amazon_fba' && l.type !== 'supplier')
  const locKey = id => (id && fbaLoc && id === fbaLoc.id ? 'fba' : id && whLoc && id === whLoc.id ? 'wh' : null)

  const snapFor = id => (snaps.data || []).find(s => s.location_id === id)?.as_of_date || null
  const asOf = { wh: snapFor(whLoc?.id), fba: snapFor(fbaLoc?.id) }
  const planDate = [asOf.wh, asOf.fba].filter(Boolean).sort().pop() || today()

  const onHand = {}
  ;(oh.data || []).forEach(r => {
    const k = locKey(r.location_id); if (!k) return
    onHand[r.product_id] = onHand[r.product_id] || {}
    onHand[r.product_id][k] = (onHand[r.product_id][k] || 0) + (Number(r.on_hand) || 0)
  })

  const forecast = fi.data ? await fetchAll(() => supabase.from('sales_forecasts').select('product_id, sku, channel, month, units').eq('import_id', fi.data.id).order('id')) : []

  // Firm supply
  const firm = []
  const pastDue = []
  const firmPOLines = []
  const committedPOs = []
  const committedTransfers = []
  const supplierName = id => (sup.data || []).find(x => x.id === id)?.name || '—'
  const locName = k => (k === 'fba' ? fbaLoc?.name || 'FBA' : whLoc?.name || 'Warehouse')
  // returns true when the line is dated before the snapshot (assumed already received)
  const add = (f, label) => {
    const cutoff = asOf[f.loc] || planDate
    if (f.qty > 0 && f.date && f.date < cutoff) { pastDue.push({ ...f, label }); return true } // assumed already in the uploaded on-hand
    firm.push({ ...f, date: f.date || planDate })
    return false
  }
  // Latest FBA upload, incl. Amazon's own inbound (shipped to FBA, not yet checked in)
  const fbaSnap = (snaps.data || []).find(s => s.location_id === fbaLoc?.id) || null
  const hasInbound = !!(fbaSnap && fbaSnap.inbound && typeof fbaSnap.inbound === 'object')
  const shipsByPo = {}
  ;(ships.data || []).forEach(s => { if (s.po_id) (shipsByPo[s.po_id] = shipsByPo[s.po_id] || []).push(s) })
  ;(pos.data || []).forEach(po => {
    (po.po_line_items || []).forEach(li => {
      if (!li.product_id) {
        // Non-product line (setup, micro, freight): cash only, if the PO isn't fully received
        const amt = (Number(li.quantity_ordered) || 0) * (Number(li.unit_cost) || 0)
        if (amt) firmPOLines.push({ fee: true, status: po.status, po_number: po.po_number, supplier_id: po.supplier_id, order_date: po.order_date, expected_date: po.expected_date, description: li.description || li.sku || 'Fee', amount: amt })
        return
      }
      if (!productById[li.product_id]) return
      // Component lines (tubes, cartons…) are cash only, like fee lines: never planned as stock
      if (productById[li.product_id].product_type === 'component') {
        const amt = Math.max(0, (Number(li.quantity_ordered) || 0) - (Number(li.quantity_received) || 0)) * (Number(li.unit_cost) || 0)
        if (amt) firmPOLines.push({ fee: true, status: po.status, po_number: po.po_number, supplier_id: po.supplier_id, order_date: po.order_date, expected_date: po.expected_date, description: productById[li.product_id].sku || li.description || 'Component', amount: amt })
        return
      }
      const open = Math.max(0, (Number(li.quantity_ordered) || 0) - (Number(li.quantity_received) || 0))
      if (!open) return
      firmPOLines.push({ status: po.status, po_number: po.po_number, supplier_id: po.supplier_id, order_date: po.order_date, expected_date: po.expected_date, product_id: li.product_id, unit_cost: li.unit_cost, open })
      let assigned = 0
      const parts = []
      let lineDue = false
      ;(shipsByPo[po.id] || []).forEach(s => {
        // match by PO line; older shipment lines have no PO line link, so fall back to the product
        (s.shipment_line_items || []).filter(sl => (sl.po_line_item_id ? sl.po_line_item_id === li.id : sl.product_id && sl.product_id === li.product_id)).forEach(sl => {
          const q = Math.max(0, (Number(sl.quantity_shipped ?? sl.total_units ?? sl.quantity_ordered) || 0) - (Number(sl.quantity_received) || 0))
          if (!q) return
          assigned += q
          const loc = s.is_direct_ship ? null : locKey(s.ship_to_location_id)
          if (!loc) { parts.push(`${fmtN(q)} direct ship`); return }
          // Shipped straight to FBA before the latest FBA upload: Amazon's inbound already includes it (counted below)
          const shipDate = s.actual_ship_date || s.expected_ship_date
          if (loc === 'fba' && hasInbound && SHIPPED_STATUSES.includes(s.status) && shipDate && shipDate < fbaSnap.as_of_date) { parts.push(`${fmtN(q)} → ${locName(loc)} (in Amazon inbound)`); return }
          const d = s.expected_arrival || po.expected_date
          if (add({ product_id: li.product_id, loc, qty: q, date: d, kind: 'po', ref: po.po_number, poStatus: poBaseStatus(po.status) }, `PO ${po.po_number} shipment`)) lineDue = true
          parts.push(`${fmtN(q)} → ${locName(loc)}${d ? ` (${fmtD(d)})` : ''}`)
        })
      })
      const rest = open - assigned
      if (rest > 0) {
        const prod = productById[li.product_id]
        const loc = prod.default_destination === 'fba' ? 'fba' : 'wh'
        // Split products: the Planner decides the FBA / warehouse split for the unsplit remainder (see planWithOrderCycles)
        const lotId = isSplitProduct(prod) ? `${po.id}|${li.id}` : undefined
        if (add({ product_id: li.product_id, loc, qty: rest, date: po.expected_date, kind: 'po', ref: po.po_number, poStatus: poBaseStatus(po.status), ...(lotId ? { lotId } : {}) }, `PO ${po.po_number}`)) lineDue = true
        parts.push(lotId ? { lotId, qty: rest } : `${fmtN(rest)} → ${locName(loc)} (not split yet)`)
      }
      committedPOs.push({ po_id: po.id, po_number: po.po_number, status: poBaseStatus(po.status), supplier: supplierName(po.supplier_id), product: productById[li.product_id], open, expected_date: po.expected_date, split: parts.filter(x => typeof x === 'string').join(' · '), lot: parts.find(x => typeof x === 'object') || null, pastDue: lineDue })
    })
  })
  // Amazon's own inbound (shipped to FBA, not yet checked in) from the latest FBA upload
  let amazonInbound = null
  if (hasInbound && fbaLoc) {
    // received = already at Amazon, checking in → counts from the report date; shipped = in transit → +AMAZON_CHECKIN_DAYS
    const clamp = d => (d < planDate ? planDate : d)
    const dates = { received: clamp(fbaSnap.as_of_date), shipped: clamp(addDays(fbaSnap.as_of_date, AMAZON_CHECKIN_DAYS)) }
    const lines = []
    Object.entries(fbaSnap.inbound).forEach(([pid, v]) => {
      if (!productById[pid]) return
      const parts = typeof v === 'object' && v ? { received: Number(v.received) || 0, shipped: Number(v.shipped) || 0 } : { received: 0, shipped: Number(v) || 0 }
      ;['received', 'shipped'].forEach(kind => {
        const qty = parts[kind]
        if (!qty) return
        firm.push({ product_id: pid, loc: 'fba', qty, date: dates[kind], kind: 'amazon_inbound', ref: `Amazon inbound (${kind})` })
        lines.push({ product: productById[pid], qty, kind, land: dates[kind] })
      })
    })
    if (lines.length) amazonInbound = { asOf: fbaSnap.as_of_date, lines, total: lines.reduce((a, l) => a + l.qty, 0) }
  }

  const today0 = today()
  const latePlanned = []
  ;(tos.data || []).forEach(t => {
    const from = locKey(t.from_location_id), to = locKey(t.to_location_id)
    const shipped = t.status === 'shipped'
    const shipDate = (shipped ? t.ship_date || t.expected_ship_date : t.expected_ship_date || t.ship_date) || planDate
    const arrive = t.expected_arrival || addDays(shipDate, TRANSFER_DAYS)
    // Does this transfer still need counting at each end?
    const countOut = !!from && (!shipped || (asOf[from] ? shipDate > asOf[from] : true))
    let countIn = !!to
    if (to && shipped) {
      if (to === 'fba' && hasInbound) countIn = shipDate > fbaSnap.as_of_date        // older ones are in Amazon's inbound
      else countIn = asOf[to] ? arrive > asOf[to] : true                              // no inbound data: assume landed by its arrival date
    }
    if (!countOut && !countIn) return
    const tLines = (t.transfer_order_lines || []).map(l => ({ product: productById[l.product_id], qty: Math.max(0, Number(l.quantity) || 0) })).filter(l => l.product && l.qty)
    if (!tLines.length) return
    if (!shipped && shipDate < today0) latePlanned.push(t.transfer_number)
    committedTransfers.push({ id: t.id, number: t.transfer_number, status: t.status, from: from ? locName(from) : '—', to: to ? locName(to) : '—', ship: shipDate, arrive, lines: tLines, total: tLines.reduce((a, l) => a + l.qty, 0), counts: countOut && countIn ? 'both' : countOut ? 'out' : 'in' })
    tLines.forEach(l => {
      if (countOut) firm.push({ product_id: l.product.id, loc: from, qty: -l.qty, date: shipDate < planDate ? planDate : shipDate, kind: 'transfer_out', ref: t.transfer_number })
      if (countIn) firm.push({ product_id: l.product.id, loc: to, qty: l.qty, date: arrive < planDate ? planDate : arrive, kind: 'transfer_in', ref: t.transfer_number })
    })
  })

  // Which POs are Completed (every part landed) — shared with the Purchase Orders pages
  let poState = {}
  try { poState = (await loadOrderStates(brandId)).poState } catch (e) { /* committed list just shows everything */ }

  return {
    products, suppliers: sup.data || [], bom: bom.data || [], onHand, forecast, firm, firmPOLines, pastDue, committedPOs, committedTransfers, amazonInbound, hasInbound, latePlanned,
    planDate, asOf, fbaLoc, whLoc, activeForecast: fi.data, poState,
  }
}

// ── page ─────────────────────────────────────────────────────────────────────
export default function Planner() {
  const { activeBrand, user } = useAuth()
  const isBrandAdmin = useIsBrandAdmin()
  const [inputs, setInputs] = useState(null)
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(true)
  const [tab, setTab] = useState('heatmap')
  const [notice, setNotice] = useState(null)

  useEffect(() => { if (activeBrand) load() }, [activeBrand?.id])

  async function load() {
    setLoading(true); setError(null)
    try { setInputs(await loadInputs(activeBrand.id)) } catch (e) { setError(e.message || String(e)) }
    setLoading(false)
  }

  // Two runs of the same engine:
  //   plan (current)  — stock + POs/transfers already in the app, nothing new → heatmap and shortage card (the risk)
  //   recs            — plus recommended orders and transfers → Reorder List and Transfer Plan (the actions)
  const plans = useMemo(() => {
    if (!inputs || !inputs.forecast.length) return null
    const base = { ...inputs, options: { warehouseBasis: WAREHOUSE_BASIS, priority: SHORTAGE_PRIORITY[activeBrand?.slug] || 'direct' } }
    const recs = planWithOrderCycles(base)
    // The current flow assumes open, unsplit POs of split products follow the recommended FBA / warehouse split
    const firm = applyLotSplits(inputs.firm, recs.lotSplits)
    return {
      current: runPlan({ ...base, firm, options: { ...base.options, recommend: false } }),
      recs,
      // Same as current, minus open supplier POs (Amazon inbound and transfers stay): months that are fine in
      // `current` but short here are only covered because an open PO arrives on time — chase the supplier.
      noPO: runPlan({ ...base, firm: firm.filter(f => f.kind !== 'po'), options: { ...base.options, recommend: false } }),
      poFirm: firm.filter(f => f.kind === 'po'),
    }
  }, [inputs, activeBrand?.slug])
  const plan = plans?.current
  const recs = plans?.recs

  const cash = useMemo(() => {
    if (!plan) return null
    // Cash Out = confirmed POs only (no recommended or draft orders) — decided Oct 2026
    return cashSchedule({ planDate: plan.planDate, months: plan.months, orders: [], firmPOLines: inputs.firmPOLines.filter(l => CASH_PO_STATUSES.includes(l.status)), products: inputs.products, suppliers: inputs.suppliers, bom: inputs.bom })
  }, [plan, inputs])

  if (!activeBrand) return null

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>Planner<HelpLink section="planner" /></h1>
          <div style={{ fontSize: '13px', color: 'var(--text-3)', marginTop: '4px' }}>
            {inputs ? <>Plan date {fmtD(inputs.planDate)} · {inputs.whLoc?.name || 'Warehouse'} as of {fmtD(inputs.asOf.wh)} · FBA as of {fmtD(inputs.asOf.fba)} · forecast {inputs.activeForecast ? `uploaded ${fmtD(inputs.activeForecast.created_at.slice(0, 10))}` : 'missing'}</> : 'Weeks-of-supply replenishment plan'}
          </div>
        </div>
        <div className="page-header-actions">
          <Link to="/planning-inputs" className="btn btn-secondary">Update inputs</Link>
          <button className="btn btn-ghost" onClick={load} title="Recalculate"><RefreshCw size={14} /></button>
        </div>
      </div>

      {loading ? <div style={{ padding: '40px', textAlign: 'center' }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
        : error ? <div className="alert alert-danger"><AlertTriangle size={14} /> {error}</div>
        : !plan ? <div className="alert alert-warning"><AlertTriangle size={14} /> No active forecast yet. Upload one on <Link to="/planning-inputs">Planning Inputs</Link>.</div>
        : (
          <>
            {!isBrandAdmin && <Summary plan={plan} recs={recs} cash={cash} />}
            <SetupWarnings plan={recs} cash={cash} inputs={inputs} />
            {notice && <div className={`alert ${notice.error ? 'alert-danger' : 'alert-success'}`} style={{ marginBottom: '16px' }}>{notice.error ? <AlertTriangle size={14} /> : <CheckCircle2 size={14} />} {notice.text}</div>}

            <div style={{ display: 'flex', gap: '4px', borderBottom: '1px solid var(--border)', marginBottom: '16px' }}>
              {TABS.map(t => (
                <button key={t.key} onClick={() => setTab(t.key)} style={{ padding: '8px 16px', background: 'none', border: 'none', borderBottom: `2px solid ${tab === t.key ? 'var(--accent)' : 'transparent'}`, color: tab === t.key ? 'var(--accent)' : 'var(--text-3)', fontFamily: 'var(--font)', fontSize: '13px', fontWeight: 500, cursor: 'pointer', marginBottom: '-1px' }}>
                  {t.label}
                </button>
              ))}
            </div>

            {tab === 'heatmap' && <Heatmap plan={plan} noPO={plans.noPO} poFirm={plans.poFirm} whName={inputs.whLoc?.name || 'Warehouse'} />}
            {tab === 'orders' && <CommittedPOs inputs={inputs} lotSplits={recs.lotSplits} />}
            {tab === 'orders' && <Orders plan={recs} inputs={inputs} brand={activeBrand} user={user} onDone={async n => { setNotice(n); await load() }} />}
            {tab === 'transfers' && <CommittedTransfers inputs={inputs} />}
            {tab === 'transfers' && <Transfers plan={recs} inputs={inputs} brand={activeBrand} user={user} onDone={async n => { setNotice(n); await load() }} />}
            {tab === 'cash' && <Cash plan={plan} cash={cash} />}
            {tab === 'forecast' && <ForecastView inputs={inputs} />}
          </>
        )}
    </div>
  )
}

function Segmented({ value, onChange, options }) {
  return (
    <div style={{ display: 'inline-flex', border: '1px solid var(--border)', borderRadius: 'var(--radius)', overflow: 'hidden' }}>
      {options.map(o => (
        <button key={o.value} onClick={() => onChange(o.value)} title={o.title} style={{ padding: '6px 12px', border: 'none', background: value === o.value ? 'var(--accent-bg)' : 'transparent', color: value === o.value ? 'var(--accent)' : 'var(--text-2)', fontFamily: 'var(--font)', fontSize: '12px', fontWeight: 500, cursor: 'pointer' }}>{o.label}</button>
      ))}
    </div>
  )
}

function Summary({ plan, recs, cash }) {
  const shortSkus = plan.rows.filter(r => r.unitsShort > 0.5)
  const unitsShort = plan.rows.reduce((a, r) => a + r.unitsShort, 0)
  const stillShort = recs.rows.filter(r => r.unitsShort > 0.5).length
  const orderUnits = recs.orders.reduce((a, o) => a + o.qty, 0)
  const expedites = recs.orders.filter(o => o.expedite).length
  const nextTransfer = recs.transfers.filter(t => t.qty > 0).sort((a, b) => a.shipBy.localeCompare(b.shipBy))[0]
  const next3Cash = cash ? plan.months.slice(0, 3).reduce((a, m) => a + (cash.byMonth[m] || 0), 0) : 0
  return (
    <div className="stat-grid" style={{ marginBottom: '16px' }}>
      <div className="stat-card"><div className="stat-label">SKUs that run short</div><div className="stat-value" style={{ color: shortSkus.length ? 'var(--danger)' : 'var(--success)' }}>{shortSkus.length}</div><div className="stat-sub">{fmtN(unitsShort)} units short with no new orders · {stillShort} still short after recommendations</div></div>
      {<div className="stat-card"><div className="stat-label">Recommended order units</div><div className="stat-value">{fmtN(orderUnits)}</div><div className="stat-sub">{recs.orders.length} order lines{expedites ? ` · ${expedites} need expediting` : ''}</div></div>}
      {<div className="stat-card"><div className="stat-label">Next transfer to FBA</div><div className="stat-value" style={{ fontSize: '20px' }}>{nextTransfer ? `Ship by ${format(parseISO(nextTransfer.shipBy), 'MMM d')}` : 'None needed'}</div><div className="stat-sub">{nextTransfer ? `${fmtN(recs.transfers.filter(t => t.shipBy === nextTransfer.shipBy).reduce((a, t) => a + t.qty, 0))} units, lands ${fmtD(nextTransfer.landDate)}` : '—'}</div></div>}
      <div className="stat-card"><div className="stat-label">Cash out, next 3 months</div><div className="stat-value">{fmt$(next3Cash)}</div><div className="stat-sub">{cash?.missing.length ? `${cash.missing.length} PO lines missing cost/terms` : 'Confirmed POs only'}</div></div>
    </div>
  )
}

function SetupWarnings({ plan, cash, inputs }) {
  const [open, setOpen] = useState(false)
  const noLead = plan.rows.filter(r => !r.isBundle && r.product.supplier_id && !r.timing.lead)
  const noSupplier = plan.rows.filter(r => !r.isBundle && !r.product.supplier_id)
  const nameOf = sku => inputs.products.find(p => p.sku === sku)?.name || sku
  const items = []
  if (noSupplier.length) items.push(`${noSupplier.length} SKUs have no supplier (12-week lead time placeholder used): ${noSupplier.map(r => r.product.name).join(', ')}`)
  if (noLead.length) items.push(`${noLead.length} SKUs have a supplier with no lead times (12-week lead time placeholder used): ${noLead.map(r => r.product.name).join(', ')}`)
  if (cash?.missing.length) items.push(`Cash out excludes: ${cash.missing.map(m => `${nameOf(m.sku)} (${m.reasons.join(', ')})`).join('; ')}`)
  if (inputs.latePlanned.length) items.push(`${inputs.latePlanned.length} planned transfer${inputs.latePlanned.length > 1 ? 's are' : ' is'} past the ship date — mark shipped or cancel in Transfer Orders: ${inputs.latePlanned.join(', ')}`)
  if (inputs.fbaLoc && !inputs.hasInbound) items.push('The latest FBA upload was saved before Amazon inbound was tracked — re-upload the FBA Inventory report so stock already on its way to Amazon is counted correctly')
  // Confirmed POs complete on their own once they land; a Draft past its date was probably never placed
  const draftsDue = [...new Set(inputs.pastDue.filter(p => p.poStatus === 'draft').map(p => p.ref))]
  if (draftsDue.length) items.push(`${draftsDue.length} draft PO${draftsDue.length > 1 ? 's are' : ' is'} past the expected date and not counted as supply — confirm (and update the date) or cancel on Purchase Orders: ${draftsDue.join(', ')}`)
  if (!items.length) return null
  return (
    <div className="alert alert-warning" style={{ marginBottom: '16px', display: 'block' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px' }}>
        <span><AlertTriangle size={14} style={{ verticalAlign: '-2px', marginRight: '6px' }} />{items.length} setup item{items.length > 1 ? 's' : ''} affect the plan</span>
        <button className="btn btn-ghost btn-sm" onClick={() => setOpen(o => !o)}>{open ? 'Hide' : 'Show'}</button>
      </div>
      {open && <ul style={{ margin: '10px 0 0', paddingLeft: '18px', fontSize: '12px', lineHeight: 1.6 }}>{items.map((t, i) => <li key={i}>{t}</li>)}</ul>}
    </div>
  )
}

// ── heatmap ──────────────────────────────────────────────────────────────────
function cellStyle(short, w, target, low = LOW_WOS, depends = false) {
  if (short > 0.5) return { background: 'var(--danger-bg)', color: 'var(--danger)', fontWeight: 600 }
  // Under 8 weeks wins over purple: running low even if the PO arrives needs a new order
  if (w !== null && w < low - 0.05) return { background: 'var(--warning-bg)', color: 'var(--warning)' }
  // Covered only because an open PO lands on time (would be short without it): follow up with the supplier
  if (depends) return { background: 'var(--violet-bg)', color: 'var(--violet)', fontWeight: 600 }
  if (w !== null && w > target * 2) return { color: 'var(--info)' }
  return {}
}

// Frozen left columns on the heatmap: SKU, Description, ASIN, Location stay visible while months scroll
const FROZEN = { sku: { left: 0, width: 140 }, name: { left: 140, width: 220 }, asin: { left: 360, width: 120 }, loc: { left: 480, width: 100 } }
const freeze = (k, header) => ({
  position: 'sticky', left: FROZEN[k].left, width: FROZEN[k].width, minWidth: FROZEN[k].width, maxWidth: FROZEN[k].width,
  background: 'var(--bg-2)', zIndex: header ? 3 : 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  ...(k === 'loc' ? { boxShadow: 'inset -1px 0 0 var(--border)' } : {}),
})

function Notes({ items }) {
  return (
    <ul style={{ margin: 0, padding: '10px 16px 12px 32px', borderBottom: '1px solid var(--border)', display: 'flex', flexDirection: 'column', gap: '3px', fontSize: '12px', color: 'var(--text-3)', lineHeight: 1.5 }}>
      {items.map((t, i) => <li key={i}>{t}</li>)}
    </ul>
  )
}

// Shipment header rows stick just under the column header row while scrolling (approx. header height)
const GROUP_TOP = 38

// Text filter: every word must appear in the SKU, description or ASIN ("delight" → Delight + Delight 2-pack).
// Wrapped by searchFilter (lib/search): a complete SKU or ASIN shows only that product.
function matchesQuery(p, q) {
  const words = String(q || '').toLowerCase().split(/\s+/).filter(Boolean)
  if (!words.length) return true
  const hay = `${p.sku || ''} ${p.name || ''} ${p.asin || ''}`.toLowerCase()
  return words.every(w => hay.includes(w))
}

function SearchBox({ value, onChange }) {
  return (
    <div style={{ position: 'relative', width: '320px' }}>
      <Search size={14} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-3)' }} />
      <input className="input" style={{ paddingLeft: '30px', paddingRight: value ? '30px' : undefined, width: '100%' }} placeholder="Search SKU, description or ASIN" value={value} onChange={e => onChange(e.target.value)} />
      {value && <button className="btn btn-ghost btn-sm" onClick={() => onChange('')} style={{ position: 'absolute', right: '4px', top: '50%', transform: 'translateY(-50%)', padding: '2px 6px' }} title="Clear"><X size={13} /></button>}
    </div>
  )
}

// Generic sort for table rows: numbers numerically, everything else case-insensitive text; blanks last
function sortBy(rows, { key, dir }) {
  const mul = dir === 'asc' ? 1 : -1
  return [...rows].sort((a, b) => {
    const va = a[key], vb = b[key]
    if (va === null || va === undefined || va === '') return 1
    if (vb === null || vb === undefined || vb === '') return -1
    if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * mul
    return String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: 'base' }) * mul
  })
}

function SortTh({ col, label, sort, setSort, style }) {
  const active = sort.key === col
  return (
    <th onClick={() => setSort(s => ({ key: col, dir: s.key === col && s.dir === 'asc' ? 'desc' : 'asc' }))} style={{ cursor: 'pointer', whiteSpace: 'nowrap', userSelect: 'none', color: active ? 'var(--accent)' : undefined, ...style }}>
      {label}{active ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : ''}
    </th>
  )
}

function Heatmap({ plan, noPO, poFirm = [], whName }) {
  const [mode, setMode] = useState('wos')
  const [onlyIssues, setOnlyIssues] = useState(false)
  const [hideSunset, setHideSunset] = useState(false)
  const [locFilter, setLocFilter] = useState('all')
  const [query, setQuery] = useState('')
  const [sort, setSort] = useState({ key: 'sku', dir: 'asc' })

  const lines = []
  const sortedRows = [...plan.rows].sort((a, b) => {
    const va = String(a.product[sort.key] || '').toLowerCase(), vb = String(b.product[sort.key] || '').toLowerCase()
    return (va < vb ? -1 : va > vb ? 1 : 0) * (sort.dir === 'asc' ? 1 : -1)
  })
  const searchMatch = searchFilter(plan.rows, query, (r, q) => matchesQuery(r.product, q), r => r.product)
  sortedRows.forEach(r => {
    if (!searchMatch(r)) return
    if (hideSunset && r.timing.sunset) return
    if (locFilter !== 'wh' && r.hasFbaActivity) lines.push({ r, loc: 'fba', label: 'FBA', d: r.fba, target: r.timing.fbaTargetWeeks, low: LOW_WOS })
    if (locFilter !== 'fba' && r.hasWhActivity) lines.push({ r, loc: 'wh', label: whName, d: r.wh, target: Number(r.product.target_wos_warehouse) || 12, low: LOW_WOS })
  })
  // Without-open-PO run, by product, to spot months that only work because a PO arrives on time
  const noPOById = useMemo(() => Object.fromEntries((noPO?.rows || []).map(r => [r.product.id, r])), [noPO])
  // Purple when the month only looks healthy because of open POs: without them it would run out or drop under the
  // low line (amber / red already win in cellStyle when the month is low or short even with the POs)
  const noPOAt = (l, i) => noPOById[l.r.product.id]?.[l.loc]
  const dependsOn = (l, i) => {
    if (l.d.short[i] > 0.5) return false
    const n = noPOAt(l, i)
    if (!n) return false
    if (n.short[i] > 0.5) return true
    const w = n.wos[i]
    return w !== null && w < l.low - 0.05
  }
  const withoutNote = (l, i) => {
    const n = noPOAt(l, i)
    return n.short[i] > 0.5 ? `short ${fmtN(n.short[i])} units` : `${n.wos[i].toFixed(1)} weeks of supply`
  }
  // Open POs landing at this product/location by the end of month i (listed on hover)
  const posFor = (l, i) => {
    const end = plan.months[i + 1] || '9999-12-31'
    const byRef = {}
    poFirm.filter(f => f.product_id === l.r.product.id && f.loc === l.loc && f.qty > 0 && f.date < end).forEach(f => {
      byRef[f.ref] = byRef[f.ref] || { ref: f.ref, qty: 0, date: f.date }
      byRef[f.ref].qty += f.qty
      if (f.date < byRef[f.ref].date) byRef[f.ref].date = f.date
    })
    return Object.values(byRef).sort((a, b) => a.date.localeCompare(b.date))
  }
  const shown = onlyIssues ? lines.filter(l => l.d.short.some(x => x > 0.5) || l.d.wos.some(w => w !== null && w < l.low - 0.05) || plan.months.some((_, i) => dependsOn(l, i))) : lines
  return (
    <div className="card">
      <div style={{ padding: '12px 16px', display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap', borderBottom: '1px solid var(--border)' }}>
        <SearchBox value={query} onChange={setQuery} />
        <Segmented value={locFilter} onChange={setLocFilter} options={[{ value: 'all', label: 'All locations' }, { value: 'fba', label: 'FBA' }, { value: 'wh', label: whName }]} />
        <Segmented value={mode} onChange={setMode} options={[{ value: 'wos', label: 'Weeks of supply' }, { value: 'units', label: 'Month-end units' }]} />
        <label style={{ fontSize: '12px', color: 'var(--text-2)', display: 'flex', gap: '6px', alignItems: 'center', cursor: 'pointer' }}><input type="checkbox" checked={onlyIssues} onChange={e => setOnlyIssues(e.target.checked)} /> Only rows with issues</label>
        <label style={{ fontSize: '12px', color: 'var(--text-2)', display: 'flex', gap: '6px', alignItems: 'center', cursor: 'pointer' }}><input type="checkbox" checked={hideSunset} onChange={e => setHideSunset(e.target.checked)} /> Hide sunset items</label>
        <button className="btn btn-ghost btn-sm" style={{ marginLeft: 'auto' }} onClick={() => downloadCSV(`wos-heatmap-${plan.planDate}.csv`, ['SKU', 'Description', 'ASIN', 'Location', ...plan.months.flatMap(m => [`${m} end units`, `${m} WOS`, `${m} short`])], shown.map(l => [l.r.product.sku, l.r.product.name, l.r.product.asin || '', l.label, ...plan.months.flatMap((_, i) => [Math.round(l.d.end[i]), l.d.wos[i] === null ? '' : l.d.wos[i].toFixed(1), Math.round(l.d.short[i])])]))}><Download size={13} /> CSV</button>
        <div style={{ flexBasis: '100%', fontSize: '12px', color: 'var(--text-3)' }}>
          <span style={{ color: 'var(--danger)' }}>■</span> out of stock, demand unmet ({mode === 'wos' ? 'shows 0 weeks; hover for units short' : 'shows −units short'}) · <span style={{ color: 'var(--violet)' }}>■</span> healthy only because of an open PO (low or out without it; hover for which) · <span style={{ color: 'var(--warning)' }}>■</span> under {LOW_WOS} weeks · <span style={{ color: 'var(--info)' }}>■</span> over 2× target · {plan.months[0] && `${fmtM(plan.months[0])} is prorated from the plan date`} · click SKU, Description or ASIN to sort
        </div>
      </div>
      <div className="table-wrap" style={{ maxHeight: '70vh', overflow: 'auto' }}>
        <table style={{ fontSize: '12px' }}>
          <thead style={{ position: 'sticky', top: 0, zIndex: 4, background: 'var(--bg-2)' }}>
            <tr>
              <SortTh col="sku" label="SKU" sort={sort} setSort={setSort} style={freeze('sku', true)} />
              <SortTh col="name" label="Description" sort={sort} setSort={setSort} style={freeze('name', true)} />
              <SortTh col="asin" label="ASIN" sort={sort} setSort={setSort} style={freeze('asin', true)} />
              <th style={freeze('loc', true)}>Location</th><th style={{ textAlign: 'right' }}>Target</th>
              {plan.months.map(m => <th key={m} style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>{fmtM(m)}</th>)}
              <th style={{ whiteSpace: 'nowrap' }}>First short</th>
            </tr>
          </thead>
          <tbody>
            {shown.length === 0 && <tr><td colSpan={plan.months.length + 6} style={{ textAlign: 'center', color: 'var(--text-3)', padding: '24px' }}>No rows match these filters</td></tr>}
            {shown.map(l => {
              const firstShort = l.d.short.findIndex(x => x > 0.5)
              return (
                <tr key={l.r.product.id + l.loc}>
                  <td style={{ ...freeze('sku'), fontFamily: 'var(--mono)' }} title={l.r.product.sku}>{l.r.product.sku}{l.r.isBundle && <div style={{ fontFamily: 'var(--font)', fontSize: '10px', color: 'var(--info)', lineHeight: 1.2 }}>2-pack</div>}{l.r.timing.sunset && <div style={{ fontFamily: 'var(--font)', fontSize: '10px', color: 'var(--text-3)', lineHeight: 1.2 }}>Sunset — no reorders</div>}{l.r.timing.split && <div style={{ fontFamily: 'var(--font)', fontSize: '10px', color: 'var(--info)', lineHeight: 1.2 }}>Split: FBA direct</div>}</td>
                  <td title={l.r.product.name} style={{ ...freeze('name'), color: 'var(--text-2)' }}>{l.r.product.name}</td>
                  <td style={{ ...freeze('asin'), fontFamily: 'var(--mono)', color: 'var(--text-2)' }}>{l.r.product.asin || '—'}</td>
                  <td style={freeze('loc')}>{l.label}</td>
                  <td style={{ textAlign: 'right', color: 'var(--text-3)' }}>{l.target}w</td>
                  {plan.months.map((m, i) => {
                    const w = l.d.wos[i]
                    // Weeks view stays in weeks: a short month reads 0 (red); units short are on hover and in the units view
                    const isShort = l.d.short[i] > 0.5
                    const val = mode === 'wos' ? (isShort ? '0' : w === null ? (l.d.end[i] > 0 ? '∞' : '—') : w.toFixed(0)) : (isShort ? `−${fmtN(l.d.short[i])}` : fmtN(l.d.end[i]))
                    const depends = dependsOn(l, i)
                    const poNote = depends ? `Depends on open PO${posFor(l, i).length > 1 ? 's' : ''}: ${posFor(l, i).map(p => `${p.ref} (${fmtN(p.qty)}, due ${fmtD(p.date)})`).join(', ') || 'see Reorder List → Committed'}. Without ${posFor(l, i).length > 1 ? 'them' : 'it'}: ${withoutNote(l, i)} · ` : ''
                    return <td key={m} title={`${poNote}${isShort ? `Short ${fmtN(l.d.short[i])} units · ` : ''}End ${fmtN(l.d.end[i])} · demand ${fmtN(l.d.demand[i])} · in ${fmtN(l.d.firmIn[i] + l.d.recIn[i])}`} style={{ textAlign: 'right', ...cellStyle(l.d.short[i], w, l.target, l.low, depends) }}>{val}</td>
                  })}
                  <td>{firstShort >= 0 ? <span className="badge badge-danger" style={{ fontSize: '10px' }}>{fmtM(plan.months[firstShort])}</span> : <span style={{ color: 'var(--text-3)' }}>—</span>}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}


// ── committed (already decided) ──────────────────────────────────────────────
function CommittedSection({ title, count, children, empty }) {
  const [open, setOpen] = useState(true)
  return (
    <div className="card" style={{ marginBottom: '16px' }}>
      <div style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', gap: '10px', borderBottom: open && count ? '1px solid var(--border)' : 'none' }}>
        <span style={{ fontSize: '13px', fontWeight: 600 }}>Committed</span>
        <span className="badge badge-neutral" style={{ fontSize: '10px' }}>{count}</span>
        <span style={{ fontSize: '12px', color: 'var(--text-3)' }}>{title}</span>
        {count > 0 && <button className="btn btn-ghost btn-sm" style={{ marginLeft: 'auto' }} onClick={() => setOpen(o => !o)}>{open ? 'Hide' : 'Show'}</button>}
      </div>
      {!count ? <div style={{ padding: '0 16px 12px', fontSize: '12px', color: 'var(--text-3)' }}>{empty}</div> : open && children}
    </div>
  )
}

const STATUS_BADGE = { draft: 'neutral', sent: 'info', confirmed: 'info', partially_received: 'warning', planned: 'neutral', shipped: 'accent', 'in transit': 'info', 'checking in': 'success' }
const statusBadge = st => <span className={`badge badge-${STATUS_BADGE[st] || 'neutral'}`} style={{ fontSize: '10px', textTransform: 'capitalize' }}>{String(st || '').replace('_', ' ')}</span>

function CommittedSearch({ query, setQuery, shown, total }) {
  return (
    <div style={{ padding: '8px 16px', display: 'flex', gap: '12px', alignItems: 'center', borderBottom: '1px solid var(--border)' }}>
      <SearchBox value={query} onChange={setQuery} />
      {query && <span style={{ fontSize: '12px', color: 'var(--text-3)' }}>{shown} of {total} lines</span>}
    </div>
  )
}

function CommittedPOs({ inputs, lotSplits = {} }) {
  const [query, setQuery] = useState('')
  const [sort, setSort] = useState({ key: 'expected_date', dir: 'asc' })
  // Completed POs (every part landed) drop off this list
  const all = inputs.committedPOs.filter(l => inputs.poState?.[l.po_id] !== 'completed').map(l => ({ ...l, sku: l.product.sku, name: l.product.name, asin: l.product.asin || '' }))
    .sort((a, b) => a.po_number.localeCompare(b.po_number) || a.sku.localeCompare(b.sku))
  const list = sortBy(all.filter(searchFilter(all, query, matchesQuery)), sort)
  return (
    <CommittedSection title="open POs already in the app — already counted as supply in the plan" count={all.length} empty="No open POs yet. Draft POs you create below will show here.">
      <CommittedSearch query={query} setQuery={setQuery} shown={list.length} total={all.length} />
      <div className="table-wrap" style={{ maxHeight: '40vh', overflow: 'auto' }}>
        <table style={{ fontSize: '12px' }}>
          <thead style={{ position: 'sticky', top: 0, zIndex: 2, background: 'var(--bg-2)' }}><tr>
            <SortTh col="po_number" label="PO #" sort={sort} setSort={setSort} />
            <SortTh col="status" label="Status" sort={sort} setSort={setSort} />
            <SortTh col="supplier" label="Supplier" sort={sort} setSort={setSort} />
            <SortTh col="sku" label="SKU" sort={sort} setSort={setSort} />
            <SortTh col="name" label="Description" sort={sort} setSort={setSort} />
            <SortTh col="open" label="Open qty" sort={sort} setSort={setSort} style={{ textAlign: 'right' }} />
            <SortTh col="expected_date" label="Expected" sort={sort} setSort={setSort} />
            <th>Destination</th>
          </tr></thead>
          <tbody>
            {list.map((l, i) => (
              <tr key={l.po_id + i}>
                <td><Link to={`/purchase-orders/${l.po_id}`} style={{ color: 'var(--accent)', textDecoration: 'none', fontFamily: 'var(--mono)' }}>{l.po_number}</Link></td>
                <td>{statusBadge(l.status)}</td>
                <td>{l.supplier}</td>
                <td style={{ fontFamily: 'var(--mono)', whiteSpace: 'nowrap' }}>{l.product.sku}</td>
                <td title={l.product.name} style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--text-2)' }}>{l.product.name}</td>
                <td style={{ textAlign: 'right', fontWeight: 500 }}>{fmtN(l.open)}</td>
                <td style={{ whiteSpace: 'nowrap' }}>{fmtD(l.expected_date)}{l.pastDue && (l.status === 'draft'
                  ? <span className="badge badge-warning" style={{ marginLeft: '6px', fontSize: '10px' }} title="Draft dated before the inventory upload — not counted as supply. Confirm it (and update the date) or cancel it.">past due</span>
                  : <span className="badge badge-neutral" style={{ marginLeft: '6px', fontSize: '10px' }} title="Part of this line is dated before the inventory upload, so it's assumed to be in the counts. If it hasn't arrived, update the date.">part landed</span>)}</td>
                <td style={{ color: 'var(--text-2)' }}>{[l.split, l.lot && (lotSplits[l.lot.lotId]
                  ? <span key="s" title="Not split into shipments yet — this is the Planner's recommended split, and the plan assumes it"><strong style={{ color: 'var(--info)' }}>Recommended split:</strong> {fmtN(lotSplits[l.lot.lotId].fba)} → FBA direct · {fmtN(lotSplits[l.lot.lotId].wh)} → {inputs.whLoc?.name || 'Warehouse'}</span>
                  : `${fmtN(l.lot.qty)} → ${inputs.whLoc?.name || 'Warehouse'} (not split yet)`)].filter(Boolean).reduce((acc, x, i) => (i ? [...acc, ' · ', x] : [x]), [])}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </CommittedSection>
  )
}

function CommittedTransfers({ inputs }) {
  const list = [...inputs.committedTransfers].sort((a, b) => String(a.arrive || a.ship || '9999').localeCompare(String(b.arrive || b.ship || '9999')))
  const ai = inputs.amazonInbound
  const note = t => t.counts === 'both' ? '' : t.counts === 'out' ? 'warehouse side only — already in Amazon inbound' : 'FBA side only — already out of the warehouse count'
  // one row per SKU line
  const rows = [
    ...(ai ? ai.lines.map(l => ({ key: `ai|${l.product.id}|${l.kind}`, source: 'Amazon inbound', status: l.kind === 'received' ? 'checking in' : 'in transit', product: l.product, qty: l.qty, shipNote: `per report ${fmtD(ai.asOf)}`, eta: l.land })) : []),
    ...list.flatMap(t => t.lines.map((l, i) => ({ key: `${t.id}|${i}`, source: t.number || '—', link: true, note: note(t), status: t.status, product: l.product, qty: l.qty, ship: t.ship, eta: t.arrive }))),
  ].map(r => ({ ...r, sku: r.product.sku, name: r.product.name, asin: r.product.asin || '', shipSort: r.ship || ai?.asOf || '' }))
   .sort((a, b) => a.sku.localeCompare(b.sku))
  const [query, setQuery] = useState('')
  const [sort, setSort] = useState({ key: 'eta', dir: 'asc' })
  const shown = sortBy(rows.filter(searchFilter(rows, query, matchesQuery)), sort)
  return (
    <CommittedSection title="transfers and Amazon inbound already counted as supply in the plan" count={rows.length} empty="Nothing in transit or planned. Transfers you create below will show here.">
      <div style={{ padding: '8px 16px', fontSize: '12px', color: 'var(--text-3)', borderBottom: '1px solid var(--border)' }}>
        Planned transfers come out of {inputs.whLoc?.name || 'the warehouse'} on their ship date and land at FBA on their required-by date (ETA). Once a transfer is shipped and shows up as inbound in an FBA upload, Amazon's inbound number takes over and the transfer drops off this list. Amazon inbound: units checking in count from the report date, units in transit count {AMAZON_CHECKIN_DAYS} days after it, and shipments created but not yet sent are ignored (the planned transfer covers them).
      </div>
      <CommittedSearch query={query} setQuery={setQuery} shown={shown.length} total={rows.length} />
      <div className="table-wrap" style={{ maxHeight: '40vh', overflow: 'auto' }}>
        <table style={{ fontSize: '12px' }}>
          <thead style={{ position: 'sticky', top: 0, zIndex: 2, background: 'var(--bg-2)' }}><tr>
            <SortTh col="source" label="Source" sort={sort} setSort={setSort} />
            <SortTh col="status" label="Status" sort={sort} setSort={setSort} />
            <SortTh col="sku" label="SKU" sort={sort} setSort={setSort} />
            <SortTh col="asin" label="ASIN" sort={sort} setSort={setSort} />
            <SortTh col="name" label="Description" sort={sort} setSort={setSort} />
            <SortTh col="qty" label="Units" sort={sort} setSort={setSort} style={{ textAlign: 'right' }} />
            <SortTh col="shipSort" label="Ship" sort={sort} setSort={setSort} />
            <SortTh col="eta" label="ETA" sort={sort} setSort={setSort} />
          </tr></thead>
          <tbody>
            {shown.map(r => (
              <tr key={r.key}>
                <td style={{ whiteSpace: 'nowrap' }}>{r.link
                  ? <Link to="/transfer-orders" style={{ color: 'var(--accent)', textDecoration: 'none', fontFamily: 'var(--mono)' }} title={`Open Transfer Orders${r.note ? ` · ${r.note}` : ''}`}>{r.source}{r.note && <span style={{ color: 'var(--text-3)' }}> *</span>}</Link>
                  : <span style={{ color: 'var(--text-2)' }}>{r.source}</span>}</td>
                <td>{statusBadge(r.status)}</td>
                <td style={{ fontFamily: 'var(--mono)', whiteSpace: 'nowrap' }}>{r.product.sku}</td>
                <td style={{ fontFamily: 'var(--mono)', whiteSpace: 'nowrap', color: 'var(--text-2)' }}>{r.asin || '—'}</td>
                <td title={r.product.name} style={{ maxWidth: '280px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--text-2)' }}>{r.product.name}</td>
                <td style={{ textAlign: 'right', fontWeight: 500 }}>{fmtN(r.qty)}</td>
                <td style={{ whiteSpace: 'nowrap', color: r.shipNote ? 'var(--text-3)' : undefined }}>{r.shipNote || fmtD(r.ship)}</td>
                <td style={{ whiteSpace: 'nowrap' }}>{fmtD(r.eta)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </CommittedSection>
  )
}

// ── reorder list ─────────────────────────────────────────────────────────────
function Orders({ plan, inputs, brand, user, onDone }) {
  const [sel, setSel] = useState({})
  const [busy, setBusy] = useState(false)
  const [query, setQuery] = useState('')
  const [supplierFilter, setSupplierFilter] = useState('')
  const [dueWithin, setDueWithin] = useState('60')
  const [sort, setSort] = useState({ key: 'sku', dir: 'asc' })
  const productById = useMemo(() => Object.fromEntries(inputs.products.map(p => [p.id, p])), [inputs])

  const all = plan.orders.map((o, i) => ({ ...o, key: `${o.product_id}|${o.landDate}|${o.destination}|${i}`, unitCost: Number(productById[o.product_id]?.cost_price) || null }))
  const cutoff = dueWithin === 'all' ? null : addDays(plan.planDate, Number(dueWithin))
  const searchMatch = searchFilter(all, query, matchesQuery)
  const visible = all.filter(o => searchMatch(o) && (!supplierFilter || (o.supplier_id || 'none') === supplierFilter) && (!cutoff || (o.writeBy && o.writeBy <= cutoff)))

  // One block per future PO: supplier + required-by date
  const blocks = {}
  visible.forEach(o => {
    const k = `${o.supplier_id || 'none:' + o.product_id}|${o.landDate}`
    blocks[k] = blocks[k] || { key: k, supplier_id: o.supplier_id, supplier: o.supplier_name || 'No supplier', landDate: o.landDate, writeBy: o.writeBy, expedite: false, needsLeadTime: false, coversFrom: o.coversFrom || o.month, coversTo: o.coversTo || o.month, lines: [] }
    const b = blocks[k]
    b.lines.push(o)
    if (o.writeBy && (!b.writeBy || o.writeBy < b.writeBy)) b.writeBy = o.writeBy
    b.expedite = b.expedite || o.expedite
    b.needsLeadTime = b.needsLeadTime || o.needsLeadTime
    if (o.coversTo > b.coversTo) b.coversTo = o.coversTo
  })
  const blockList = Object.values(blocks).sort((a, b) => String(a.writeBy || '9999').localeCompare(String(b.writeBy || '9999')) || a.supplier.localeCompare(b.supplier))
  const supplierOptions = [...new Map(all.map(o => [o.supplier_id || 'none', o.supplier_name || 'No supplier'])).entries()].sort((a, b) => a[1].localeCompare(b[1]))
  const chosen = all.filter(o => sel[o.key])
  const hiddenCount = all.length - visible.length

  async function createPOs() {
    setBusy(true)
    const groups = {}
    chosen.forEach(o => { const k = `${o.supplier_id}|${o.landDate}`; (groups[k] = groups[k] || []).push(o) })
    let made = 0
    for (const [k, list] of Object.entries(groups)) {
      const [supplierId, land] = k.split('|')
      const poNumber = `PL-${format(new Date(), 'yyMMdd')}-${Math.random().toString(36).slice(2, 6).toUpperCase()}`
      const covers = fmtMRange(list[0].coversFrom || land, list.reduce((a, o) => (o.coversTo > a ? o.coversTo : a), land))
      const { data: po, error } = await supabase.from('purchase_orders').insert({
        brand_id: brand.id, po_number: poNumber, supplier_id: supplierId, status: 'draft', order_date: today(), expected_date: land,
        notes: `Created from Planner (plan date ${inputs.planDate}). Covers ${covers} sales + safety stock, required by ${land}.${list.some(o => o.destination === 'split') ? ` Recommended split: ${list.filter(o => o.destination === 'split').map(o => `${o.sku} ${o.fbaQty} → FBA direct / ${o.whQty} → ${inputs.whLoc?.name || 'warehouse'}`).join('; ')}.` : ''} Split into FBA / warehouse shipments when ready to ship.`, created_by: user?.id,
      }).select().single()
      if (error) { await onDone({ error: true, text: `Creating PO failed: ${error.message}` }); setBusy(false); return }
      const { error: le } = await supabase.from('po_line_items').insert(list.map(o => ({ po_id: po.id, product_id: o.product_id, sku: o.sku, description: o.name, quantity_ordered: o.qty, unit_cost: o.unitCost })))
      if (le) { await onDone({ error: true, text: `PO ${poNumber} created but lines failed: ${le.message}` }); setBusy(false); return }
      made++
    }
    setSel({}); setBusy(false)
    await onDone({ text: `Created ${made} draft PO${made === 1 ? '' : 's'}. They now count as firm supply — review them under Purchase Orders.` })
  }

  return (
    <div className="card">
      <div style={{ padding: '12px 16px', display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap', borderBottom: '1px solid var(--border)' }}>
        <span style={{ fontSize: '13px', fontWeight: 600 }}>Recommended POs</span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px' }}>
          <button className="btn btn-ghost btn-sm" onClick={() => downloadCSV(`reorder-list-${plan.planDate}.csv`, ['Supplier', 'Write PO by', 'Required by', 'Covers from', 'Covers through', 'SKU', 'Description', 'Destination', 'Qty', 'Unit cost', 'Total cost', 'Flags'], visible.map(o => [o.supplier_name || '', o.writeBy || '', o.landDate, o.coversFrom || '', o.coversTo || '', o.sku, o.name, o.destination === 'split' ? `Split: ${o.fbaQty} FBA / ${o.whQty} warehouse` : o.destination === 'fba' ? 'FBA' : 'Warehouse', o.qty, o.unitCost ?? '', o.unitCost ? o.unitCost * o.qty : '', [o.expedite && 'Expedite', o.needsLeadTime && 'Assumed 12-week lead time', o.needsSupplier && 'Needs supplier'].filter(Boolean).join('; ')]))}><Download size={13} /> CSV</button>
          <button className="btn btn-primary btn-sm" disabled={!chosen.length || busy} onClick={createPOs}>{busy ? 'Creating…' : `Create draft PO${chosen.length ? ` (${chosen.length} lines)` : ''}`}</button>
        </div>
      </div>
      <div style={{ padding: '12px 16px', display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap', borderBottom: '1px solid var(--border)' }}>
        <SearchBox value={query} onChange={setQuery} />
        <select className="select" style={{ width: '220px' }} value={supplierFilter} onChange={e => setSupplierFilter(e.target.value)}>
          <option value="">All suppliers</option>
          {supplierOptions.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
        </select>
        <span style={{ fontSize: '12px', color: 'var(--text-3)' }}>Write PO within</span>
        <Segmented value={dueWithin} onChange={setDueWithin} options={[{ value: '30', label: '30 days' }, { value: '60', label: '60 days' }, { value: '90', label: '90 days' }, { value: 'all', label: 'All' }]} />
        {hiddenCount > 0 && <span style={{ fontSize: '12px', color: 'var(--text-3)' }}>{hiddenCount} later line{hiddenCount === 1 ? '' : 's'} hidden by filters</span>}
      </div>
      <Notes items={[
        'Each block is one PO: a supplier\'s needs for the next order cycle (3 months unless set otherwise on the supplier) combined',
        'Required by = the date stock must be available at the destination · Write PO by = Required by minus the supplier lead time',
        'Weeks of supply target: 12 weeks at FBA and WeWork',
        `Smooth 3oz and Delight 3oz ship part of each PO straight to FBA; the rest goes to WeWork (FBA max ${FBA_MAX_WEEKS} weeks)`,
        'Quantities are exact needs — round to case pack and/or MOQs as needed',
        'Finished-goods POs need matching component POs (tubes, cartons, bottles), written outside the app for now. Add them to the app to include them in the Cash Out projection',
      ]} />
      {!blockList.length ? <div className="empty-state"><CheckCircle2 size={32} /><p>{all.length ? 'No POs need writing in this window — widen “Write PO within” to see later ones' : 'No orders needed within the forecast horizon'}</p></div> : (
        <div className="table-wrap" style={{ maxHeight: '70vh', overflow: 'auto' }}>
          <table style={{ fontSize: '12px' }}>
            <thead style={{ position: 'sticky', top: 0, zIndex: 3, background: 'var(--bg-2)' }}><tr><th style={{ width: '28px' }}></th>
              <SortTh col="sku" label="SKU" sort={sort} setSort={setSort} />
              <SortTh col="name" label="Description" sort={sort} setSort={setSort} />
              <SortTh col="destination" label="Destination" sort={sort} setSort={setSort} />
              <SortTh col="qty" label="Qty" sort={sort} setSort={setSort} style={{ textAlign: 'right' }} />
            </tr></thead>
            <tbody>
              {blockList.map(b => {
                const lines = sortBy(b.lines, sort)
                const selectable = !!b.supplier_id
                const allOn = lines.every(o => sel[o.key])
                return [
                  <tr key={b.key}>
                    <td style={{ position: 'sticky', top: GROUP_TOP, zIndex: 2, background: 'var(--bg-3)' }}><input type="checkbox" disabled={!selectable} title={selectable ? 'Select this whole PO' : 'Set a supplier on these products first'} checked={selectable && allOn} onChange={e => setSel(s => { const n = { ...s }; lines.forEach(o => { n[o.key] = e.target.checked }); return n })} /></td>
                    <td colSpan={4} style={{ position: 'sticky', top: GROUP_TOP, zIndex: 2, background: 'var(--bg-3)', fontWeight: 600 }}>
                      {b.supplier} · write PO by <span style={{ color: b.expedite ? 'var(--danger)' : undefined }}>{b.writeBy ? fmtD(b.writeBy) : '—'}</span> → required by {fmtD(b.landDate)} · covers {fmtMRange(b.coversFrom, b.coversTo)} sales + safety stock · {lines.length} SKU{lines.length === 1 ? '' : 's'} · {fmtN(lines.reduce((a, o) => a + o.qty, 0))} units
                      {b.expedite && <span className="badge badge-danger" style={{ marginLeft: '8px', fontSize: '10px' }}>Expedite</span>}
                      {b.needsLeadTime && <span style={{ marginLeft: '8px', fontSize: '11px', fontWeight: 400, color: 'var(--text-3)' }}>assumed 12-wk lead</span>}
                    </td>
                  </tr>,
                  ...lines.map(o => (
                    <tr key={o.key}>
                      <td><input type="checkbox" disabled={!selectable} checked={!!sel[o.key]} onChange={e => setSel(s => ({ ...s, [o.key]: e.target.checked }))} /></td>
                      <td style={{ fontFamily: 'var(--mono)', whiteSpace: 'nowrap' }}>{o.sku}{o.topUp && <div style={{ fontFamily: 'var(--font)', fontSize: '10px', color: 'var(--text-3)', lineHeight: 1.2 }}>top-up</div>}</td>
                      <td title={o.name} style={{ maxWidth: '520px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--text-2)' }}>{o.name}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>{o.destination === 'split' ? <span title="Ship straight from the supplier to FBA, rest to the warehouse"><span style={{ color: 'var(--info)' }}>{fmtN(o.fbaQty)} FBA</span> · {fmtN(o.whQty)} {inputs.whLoc?.name || 'Warehouse'}</span> : o.destination === 'fba' ? 'FBA' : 'Warehouse'}</td>
                      <td style={{ textAlign: 'right', fontWeight: 500 }}>{fmtN(o.qty)}</td>
                    </tr>
                  )),
                ]
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

// ── transfer plan ────────────────────────────────────────────────────────────
function Transfers({ plan, inputs, brand, user, onDone }) {
  const [sort, setSort] = useState({ key: 'sku', dir: 'asc' })
  const [sel, setSel] = useState({})
  const [busy, setBusy] = useState(false)
  const [query, setQuery] = useState('')
  const productById = useMemo(() => Object.fromEntries(inputs.products.map(p => [p.id, p])), [inputs])
  // Lines with nothing to ship are hidden — their shortages show on the heatmap
  const all = plan.transfers.filter(t => t.qty > 0).map((t, i) => ({ ...t, asin: productById[t.product_id]?.asin || '', key: `${t.product_id}|${t.month}|${i}` }))
  const list = all.filter(searchFilter(all, query, matchesQuery))
  // Shipments in date order; sorting by Ship by / Required by flips the shipment order, other columns sort lines within each shipment
  const dateSort = sort.key === 'shipBy' || sort.key === 'landDate'
  const months = [...new Set(list.map(t => t.month))].sort()
  if (dateSort && sort.dir === 'desc') months.reverse()
  const chosen = list.filter(t => sel[t.key] && t.qty > 0)

  // Review step: one draft per shipment (ship-by date); everything is editable before saving
  const [review, setReview] = useState(null)
  function openReview() {
    if (!inputs.whLoc || !inputs.fbaLoc) return
    const byMonth = {}
    chosen.forEach(t => { (byMonth[t.month] = byMonth[t.month] || []).push(t) })
    setReview(Object.keys(byMonth).sort().map(m => {
      const items = byMonth[m]
      const builds = items.filter(t => t.isBundle && t.buildQty > 0).map(t => `${t.sku}: build ${t.buildQty} from ${t.components.map(c => `${c.units} × ${c.sku}`).join(' + ')}`)
      return {
        key: m,
        number: `TO-PL-${format(new Date(), 'yyMMdd')}-${Math.random().toString(36).slice(2, 5).toUpperCase()}`,
        shipBy: items[0].shipBy, landDate: items[0].landDate,
        notes: `Created from Planner (plan date ${inputs.planDate}).${builds.length ? ' 2-pack builds: ' + builds.join('; ') : ''}`,
        lines: items.map(t => ({ product_id: t.product_id, sku: t.sku, name: t.name, qty: String(t.qty), suggested: t.qty })),
      }
    }))
  }
  const setDraft = (k, patch) => setReview(r => r.map(d => (d.key === k ? { ...d, ...patch } : d)))
  const setLine = (k, i, qty) => setReview(r => r.map(d => (d.key === k ? { ...d, lines: d.lines.map((l, j) => (j === i ? { ...l, qty } : l)) } : d)))
  const dropLine = (k, i) => setReview(r => r.map(d => (d.key === k ? { ...d, lines: d.lines.filter((_, j) => j !== i) } : d)).filter(d => d.lines.length))

  async function createTransfers() {
    setBusy(true)
    let made = 0
    for (const d of review) {
      const lines = d.lines.map(l => ({ ...l, q: Number(l.qty) || 0 })).filter(l => l.q > 0)
      if (!lines.length) continue
      const { data: to, error } = await supabase.from('transfer_orders').insert({
        brand_id: brand.id, transfer_number: d.number, from_location_id: inputs.whLoc.id, to_location_id: inputs.fbaLoc.id, status: 'planned',
        expected_ship_date: d.shipBy || null, expected_arrival: d.landDate || null, created_by: user?.id, notes: d.notes || null,
      }).select().single()
      if (error) { await onDone({ error: true, text: `Creating transfer failed: ${error.message}` }); setBusy(false); return }
      const { error: le } = await supabase.from('transfer_order_lines').insert(lines.map(l => ({ transfer_order_id: to.id, product_id: l.product_id, quantity: l.q })))
      if (le) { await onDone({ error: true, text: `Transfer ${d.number} created but lines failed: ${le.message}` }); setBusy(false); return }
      made++
    }
    setSel({}); setReview(null); setBusy(false)
    await onDone({ text: `Created ${made} planned transfer${made === 1 ? '' : 's'} ${inputs.whLoc.name} → FBA. Mark them shipped under Transfer Orders once they leave.` })
  }

  return (
    <div className="card">
      <div style={{ padding: '12px 16px', display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap', borderBottom: '1px solid var(--border)' }}>
        <span style={{ fontSize: '13px', fontWeight: 600 }}>Recommended</span>
        <SearchBox value={query} onChange={setQuery} />
        {query && <span style={{ fontSize: '12px', color: 'var(--text-3)' }}>{list.length} of {all.length} lines · {fmtN(list.reduce((a, t) => a + t.qty, 0))} units</span>}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px' }}>
          <button className="btn btn-ghost btn-sm" onClick={() => downloadCSV(`transfer-plan-${plan.planDate}.csv`, ['Ship by', 'Required by', 'SKU', 'ASIN', 'Name', 'Qty', 'Wanted', 'Shortfall', '2-pack build'], list.map(t => [t.shipBy, t.landDate, t.sku, t.asin, t.name, t.qty, t.wanted, t.shortfall, t.isBundle ? t.components.map(c => `${c.units} x ${c.sku}`).join(' + ') : '']))}><Download size={13} /> CSV</button>
          <button className="btn btn-primary btn-sm" disabled={!chosen.length || busy} onClick={openReview}>{`Create planned transfer${chosen.length ? ` (${chosen.length} line${chosen.length === 1 ? '' : 's'})` : ''}`}</button>
        </div>
      </div>
      <Notes items={[
        `${inputs.whLoc?.name || 'Warehouse'} → FBA`,
        'Required by = the date stock must be available at FBA · Ship by = Required by minus 14 days',
        'Only stock already at the warehouse on the ship-by date can go',
        '2-packs are built from singles at the warehouse',
      ]} />
      {!list.length ? <div className="empty-state"><CheckCircle2 size={32} /><p>{all.length ? 'No recommended transfers match this search' : 'No transfers needed'}</p></div> : (
        <div className="table-wrap" style={{ maxHeight: '70vh', overflow: 'auto' }}>
          <table style={{ fontSize: '12px' }}>
            <thead style={{ position: 'sticky', top: 0, zIndex: 3, background: 'var(--bg-2)' }}><tr><th style={{ width: '28px' }}></th>
              <SortTh col="sku" label="SKU" sort={sort} setSort={setSort} />
              <SortTh col="asin" label="ASIN" sort={sort} setSort={setSort} />
              <SortTh col="name" label="Description" sort={sort} setSort={setSort} />
              <SortTh col="qty" label="Qty" sort={sort} setSort={setSort} style={{ textAlign: 'right' }} />
              <SortTh col="shipBy" label="Ship by" sort={sort} setSort={setSort} />
              <SortTh col="landDate" label="Required by" sort={sort} setSort={setSort} />
              <th>Notes</th>
            </tr></thead>
            <tbody>
              {months.map((m, gi) => {
                const items = sortBy(list.filter(t => t.month === m), dateSort ? { key: 'sku', dir: 'asc' } : sort)
                const allOn = items.filter(t => t.qty > 0).every(t => sel[t.key])
                return [
                  <tr key={m} style={{ background: 'var(--bg-3)' }}>
                    <td style={{ position: 'sticky', top: GROUP_TOP, zIndex: 2, background: 'var(--bg-3)' }}><input type="checkbox" checked={allOn && items.some(t => t.qty > 0)} onChange={e => setSel(s => { const n = { ...s }; items.forEach(t => { if (t.qty > 0) n[t.key] = e.target.checked }); return n })} /></td>
                    <td colSpan={7} style={{ fontWeight: 600, position: 'sticky', top: GROUP_TOP, zIndex: 2, background: 'var(--bg-3)' }}>Shipment {gi + 1} · ship by {fmtD(items[0].shipBy)} → required by {fmtD(items[0].landDate)} · {items.filter(t => t.qty > 0).length} SKUs · {fmtN(items.reduce((a, t) => a + t.qty, 0))} units</td>
                  </tr>,
                  ...items.map(t => (
                    <tr key={t.key}>
                      <td><input type="checkbox" disabled={!t.qty} checked={!!sel[t.key]} onChange={e => setSel(s => ({ ...s, [t.key]: e.target.checked }))} /></td>
                      <td style={{ fontFamily: 'var(--mono)', whiteSpace: 'nowrap' }}>{t.sku}{t.isBundle && <div style={{ fontFamily: 'var(--font)', fontSize: '10px', color: 'var(--info)', lineHeight: 1.2 }}>2-pack</div>}</td>
                      <td style={{ fontFamily: 'var(--mono)', whiteSpace: 'nowrap', color: 'var(--text-2)' }}>{t.asin || '—'}</td>
                      <td title={t.name} style={{ maxWidth: '240px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--text-2)' }}>{t.name}</td>
                      <td style={{ textAlign: 'right', fontWeight: 500 }}>{fmtN(t.qty)}</td>
                      <td>{fmtD(t.shipBy)}</td>
                      <td>{fmtD(t.landDate)}</td>
                      <td><div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                        {t.isBundle && t.buildQty > 0 && <span style={{ color: 'var(--text-2)' }}>Build {fmtN(t.buildQty)} from {t.components.map(c => `${fmtN(c.units)} × ${c.sku}`).join(' + ')}</span>}
                        {t.shortfall > 0.5 && <span className="badge badge-danger" style={{ fontSize: '10px' }}>{fmtN(t.shortfall)} short — not enough at warehouse</span>}
                      </div></td>
                    </tr>
                  )),
                ]
              })}
            </tbody>
          </table>
        </div>
      )}
      {review && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && !busy && setReview(null)}>
          <div className="modal modal-lg" style={{ maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
            <div className="modal-header">
              <h2>Review planned transfer{review.length > 1 ? `s (${review.length})` : ''}</h2>
              <button className="btn btn-ghost btn-sm" onClick={() => setReview(null)} disabled={busy}><X size={14} /></button>
            </div>
            <div className="modal-body" style={{ overflowY: 'auto' }}>
              <div style={{ fontSize: '12px', color: 'var(--text-3)' }}>{inputs.whLoc?.name} → {inputs.fbaLoc?.name} · status Planned. Edit anything below, then create.</div>
              {review.map(d => (
                <div key={d.key} className="card" style={{ padding: '14px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr 1fr', gap: '12px' }}>
                    <div className="form-group"><label className="form-label">Transfer #</label><input className="input" value={d.number} onChange={e => setDraft(d.key, { number: e.target.value })} /></div>
                    <div className="form-group"><label className="form-label">Ship by</label><input className="input" type="date" value={d.shipBy || ''} onChange={e => setDraft(d.key, { shipBy: e.target.value })} /></div>
                    <div className="form-group"><label className="form-label">Required by (ETA)</label><input className="input" type="date" value={d.landDate || ''} onChange={e => setDraft(d.key, { landDate: e.target.value })} /></div>
                  </div>
                  <table style={{ fontSize: '12px' }}>
                    <thead><tr><th>SKU</th><th>Description</th><th style={{ textAlign: 'right' }}>Suggested</th><th style={{ textAlign: 'right', width: '110px' }}>Qty</th><th style={{ width: '36px' }}></th></tr></thead>
                    <tbody>
                      {d.lines.map((l, i) => (
                        <tr key={l.product_id + i}>
                          <td style={{ fontFamily: 'var(--mono)', whiteSpace: 'nowrap' }}>{l.sku}</td>
                          <td style={{ color: 'var(--text-2)', maxWidth: '260px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={l.name}>{l.name}</td>
                          <td style={{ textAlign: 'right', color: 'var(--text-3)' }}>{fmtN(l.suggested)}</td>
                          <td><input className="input" type="number" min="0" style={{ textAlign: 'right', padding: '4px 8px' }} value={l.qty} onChange={e => setLine(d.key, i, e.target.value)} /></td>
                          <td><button className="btn btn-ghost btn-sm" title="Remove line" onClick={() => dropLine(d.key, i)}><X size={13} /></button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div style={{ fontSize: '12px', color: 'var(--text-2)', textAlign: 'right' }}>{d.lines.length} SKU{d.lines.length === 1 ? '' : 's'} · {fmtN(d.lines.reduce((a, l) => a + (Number(l.qty) || 0), 0))} units</div>
                  <div className="form-group"><label className="form-label">Notes</label><textarea className="textarea" rows={2} value={d.notes} onChange={e => setDraft(d.key, { notes: e.target.value })} /></div>
                </div>
              ))}
              {!review.length && <div style={{ fontSize: '13px', color: 'var(--text-3)' }}>All lines removed.</div>}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setReview(null)} disabled={busy}>Cancel</button>
              <button className="btn btn-primary" onClick={createTransfers} disabled={busy || !review.some(d => d.number && d.lines.some(l => Number(l.qty) > 0))}>{busy ? 'Creating…' : `Create ${review.length > 1 ? `${review.length} transfers` : 'transfer'}`}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ── active forecast (read-only view) ─────────────────────────────────────────
const CHANNEL_LABEL = { amazon: 'Amazon', direct: 'Direct', other: 'Other' }

function ForecastView({ inputs }) {
  const [query, setQuery] = useState('')
  const [channel, setChannel] = useState('all')
  const [sort, setSort] = useState({ key: 'sku', dir: 'asc' })
  const [fromMonth, setFromMonth] = useState('current')
  const productById = useMemo(() => Object.fromEntries(inputs.products.map(p => [p.id, p])), [inputs])
  const planMonth = inputs.planDate.slice(0, 7) + '-01'
  const allMonths = useMemo(() => [...new Set(inputs.forecast.map(f => f.month))].sort(), [inputs])
  const months = fromMonth === 'current' ? allMonths.filter(m => m >= planMonth) : allMonths

  const rows = useMemo(() => {
    const by = {}
    inputs.forecast.forEach(f => {
      const p = productById[f.product_id]
      const key = (f.product_id || f.sku) + '|' + f.channel
      by[key] = by[key] || { key, product_id: f.product_id, sku: p?.sku || f.sku, name: p?.name || '(not in catalog)', asin: p?.asin || '', channel: f.channel, byMonth: {}, total: 0 }
      by[key].byMonth[f.month] = (by[key].byMonth[f.month] || 0) + Number(f.units || 0)
    })
    return Object.values(by)
  }, [inputs, productById])

  const searchMatch = searchFilter(rows, query, matchesQuery)
  const filtered = rows.filter(r => searchMatch(r) && (channel === 'all' || r.channel === channel))
    .map(r => ({ ...r, total: months.reduce((a, m) => a + (r.byMonth[m] || 0), 0) }))
    .filter(r => r.total !== 0)
  const sorted = sortBy(filtered, sort)
  const totals = months.map(m => filtered.reduce((a, r) => a + (r.byMonth[m] || 0), 0))
  const f = inputs.activeForecast

  return (
    <div className="card">
      <div style={{ padding: '12px 16px', display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap', borderBottom: '1px solid var(--border)' }}>
        <SearchBox value={query} onChange={setQuery} />
        <Segmented value={channel} onChange={setChannel} options={[{ value: 'all', label: 'All channels' }, { value: 'amazon', label: 'Amazon' }, { value: 'direct', label: 'Direct' }, { value: 'other', label: 'Other' }]} />
        <Segmented value={fromMonth} onChange={setFromMonth} options={[{ value: 'current', label: 'From this month' }, { value: 'all', label: 'All months' }]} />
        <button className="btn btn-ghost btn-sm" style={{ marginLeft: 'auto' }} onClick={() => downloadCSV(`forecast-${inputs.planDate}.csv`, ['SKU', 'Description', 'ASIN', 'Channel', ...months, 'Total'], sorted.map(r => [r.sku, r.name, r.asin, CHANNEL_LABEL[r.channel], ...months.map(m => r.byMonth[m] || 0), r.total]))}><Download size={13} /> CSV</button>
        <div style={{ flexBasis: '100%', fontSize: '12px', color: 'var(--text-3)' }}>
          Active forecast{f ? ` — ${f.file_name || 'upload'}, uploaded ${fmtD(f.created_at.slice(0, 10))}` : ''}. Units per month as uploaded (the plan prorates the current month). To change it, upload a new file on <Link to="/planning-inputs">Planning Inputs</Link>.
        </div>
      </div>
      <div className="table-wrap" style={{ maxHeight: '70vh', overflow: 'auto' }}>
        <table style={{ fontSize: '12px' }}>
          <thead style={{ position: 'sticky', top: 0, zIndex: 4, background: 'var(--bg-2)' }}>
            <tr>
              <SortTh col="sku" label="SKU" sort={sort} setSort={setSort} style={freeze('sku', true)} />
              <SortTh col="name" label="Description" sort={sort} setSort={setSort} style={freeze('name', true)} />
              <SortTh col="asin" label="ASIN" sort={sort} setSort={setSort} style={freeze('asin', true)} />
              <SortTh col="channel" label="Channel" sort={sort} setSort={setSort} style={freeze('loc', true)} />
              {months.map(m => <th key={m} style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>{fmtM(m)}</th>)}
              <SortTh col="total" label="Total" sort={sort} setSort={setSort} style={{ textAlign: 'right' }} />
            </tr>
          </thead>
          <tbody>
            {sorted.length === 0 && <tr><td colSpan={months.length + 5} style={{ textAlign: 'center', color: 'var(--text-3)', padding: '24px' }}>No forecast rows match these filters</td></tr>}
            {sorted.map(r => (
              <tr key={r.key}>
                <td style={{ ...freeze('sku'), fontFamily: 'var(--mono)' }}>{r.sku}</td>
                <td title={r.name} style={{ ...freeze('name'), color: r.product_id ? 'var(--text-2)' : 'var(--warning)' }}>{r.name}</td>
                <td style={{ ...freeze('asin'), fontFamily: 'var(--mono)', color: 'var(--text-2)' }}>{r.asin || '—'}</td>
                <td style={freeze('loc')}>{CHANNEL_LABEL[r.channel]}</td>
                {months.map(m => <td key={m} style={{ textAlign: 'right', color: r.byMonth[m] ? undefined : 'var(--text-3)' }}>{fmtN(r.byMonth[m] || 0)}</td>)}
                <td style={{ textAlign: 'right', fontWeight: 600 }}>{fmtN(r.total)}</td>
              </tr>
            ))}
            {sorted.length > 1 && (
              <tr style={{ fontWeight: 600 }}>
                <td style={{ ...freeze('sku'), background: 'var(--bg-3)' }}>Total</td>
                <td style={{ ...freeze('name'), background: 'var(--bg-3)' }}></td>
                <td style={{ ...freeze('asin'), background: 'var(--bg-3)' }}></td>
                <td style={{ ...freeze('loc'), background: 'var(--bg-3)' }}>{channel === 'all' ? 'All' : CHANNEL_LABEL[channel]}</td>
                {totals.map((t, i) => <td key={i} style={{ textAlign: 'right', background: 'var(--bg-3)' }}>{fmtN(t)}</td>)}
                <td style={{ textAlign: 'right', background: 'var(--bg-3)' }}>{fmtN(totals.reduce((a, b) => a + b, 0))}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ── cash out ─────────────────────────────────────────────────────────────────
function Cash({ plan, cash }) {
  if (!cash) return null
  // Cash Out is a forward projection (invoices are tracked elsewhere): payments dated before the plan month are left out
  const lines = cash.lines.filter(l => l.date >= plan.months[0])
  const cols = plan.months
  const bySupplier = {}
  lines.forEach(l => {
    const k = l.date.slice(0, 7) + '-01'
    bySupplier[l.supplier] = bySupplier[l.supplier] || {}
    bySupplier[l.supplier][k] = (bySupplier[l.supplier][k] || 0) + l.amount
  })
  const suppliers = Object.keys(bySupplier).sort()
  const total = k => cash.byMonth[k] || 0
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {cash.missing.length > 0 && <div className="alert alert-warning"><AlertTriangle size={14} /> {cash.missing.length} confirmed PO line{cash.missing.length === 1 ? '' : 's'} excluded until the supplier has payment terms (or the line has a cost): {cash.missing.map(m => `${m.sku} (${m.reasons.join(', ')})`).join('; ')}</div>}
      <div className="card">
        <div style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', borderBottom: '1px solid var(--border)' }}>
          <span style={{ fontSize: '13px', color: 'var(--text-2)' }}>Projected deposits and balances by month from confirmed POs, from this month on. Earlier payments are left out — invoices are tracked outside the app.</span>
          <button className="btn btn-ghost btn-sm" style={{ marginLeft: 'auto' }} onClick={() => downloadCSV(`cash-out-${plan.planDate}.csv`, ['Date', 'Source', 'Reference', 'SKU', 'Component', 'Supplier', 'Type', 'Amount'], lines.map(l => [l.date, l.source, l.ref, l.sku, l.component || '', l.supplier, l.type, l.amount.toFixed(2)]))}><Download size={13} /> CSV</button>
        </div>
        <div className="table-wrap">
          <table style={{ fontSize: '12px' }}>
            <thead><tr><th>Supplier</th>{cols.map(c => <th key={c} style={{ textAlign: 'right' }}>{fmtM(c)}</th>)}<th style={{ textAlign: 'right' }}>Total</th></tr></thead>
            <tbody>
              {suppliers.map(s => (
                <tr key={s}><td>{s}</td>{cols.map(c => <td key={c} style={{ textAlign: 'right' }}>{bySupplier[s][c] ? fmt$(bySupplier[s][c]) : '—'}</td>)}<td style={{ textAlign: 'right', fontWeight: 500 }}>{fmt$(Object.values(bySupplier[s]).reduce((a, b) => a + b, 0))}</td></tr>
              ))}
              <tr style={{ fontWeight: 600, background: 'var(--bg-3)' }}><td>Total</td>{cols.map(c => <td key={c} style={{ textAlign: 'right' }}>{total(c) ? fmt$(total(c)) : '—'}</td>)}<td style={{ textAlign: 'right' }}>{fmt$(cols.reduce((a, c) => a + total(c), 0))}</td></tr>
            </tbody>
          </table>
        </div>
      </div>
      {lines.length > 0 && (
        <div className="card">
          <div style={{ padding: '12px 16px', fontSize: '13px', fontWeight: 600, borderBottom: '1px solid var(--border)' }}>Payment detail</div>
          <div className="table-wrap" style={{ maxHeight: '50vh', overflow: 'auto' }}>
            <table style={{ fontSize: '12px' }}>
              <thead><tr><th>Date</th><th>Source</th><th>Reference</th><th>SKU</th><th>Supplier</th><th>Type</th><th style={{ textAlign: 'right' }}>Amount</th></tr></thead>
              <tbody>{lines.map((l, i) => <tr key={i}><td>{fmtD(l.date)}</td><td>{l.source}</td><td>{l.ref}</td><td style={{ fontFamily: 'var(--mono)' }}>{l.sku}{l.component ? ` · ${l.component}` : ''}</td><td>{l.supplier}</td><td>{l.type}</td><td style={{ textAlign: 'right' }}>{fmt$(l.amount)}</td></tr>)}</tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}
